/* eslint-disable no-plusplus */
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { heightFix, pageContext } from 'ctm-util-functions';
import { SimpleFlexbox } from '../../../../../flexbox/flexbox';
import styles from './features_header.css';

/**
 * Custom hook to handle the resizing of existing children inside a container that uses 'balance height' (e.g. dynamic text wrapping, image loading late).
 * Note: Does not handle children being added or removed.
 * @param {string} rowId - ID used for height balancing
 * @param {boolean} isResizable - Whether to watch for resizing
 * @returns {React.RefObject} Reference to attach to the container element
 */
const useBalanceHeightOnResize = (rowId, isResizable) => {
    const rootRef = useRef(null);

    useEffect(() => {
        if (!isResizable || !rootRef.current || !rowId) return undefined;

        const resizeObserver = new ResizeObserver(() => heightFix('balanceheight', rowId));
        Array.from(rootRef.current.children).forEach((child) => resizeObserver.observe(child));

        return () => resizeObserver.disconnect();
    }, [isResizable, rowId]);

    return rootRef;
};

/**
 * Wrapper component that adds balance height functionality
 * @param {Object} props - Properties for the component
 * @param {string} props.rowId - Unique ID for all elements in a row
 * @param {string|number} [props.flex] - CSS flex value
 * @param {string} [props.justifyContent] - CSS justify-content value
 * @param {boolean} [props.isResizable] - Whether to monitor for size changes and rebalance
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactNode} - Wrapped child components with height balanced
 */
const BalanceHeightWrapper = ({ rowId, isResizable = false, flex, justifyContent, children }) => {
    const rootRef = useBalanceHeightOnResize(rowId, isResizable);

    const style = {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex,
        justifyContent
    };

    return (
        <div ref={rootRef} style={style} data-balanceheight={rowId}>
            {children}
        </div>
    );
};

BalanceHeightWrapper.propTypes = {
    rowId: PropTypes.string.isRequired,
    isResizable: PropTypes.bool,
    flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    justifyContent: PropTypes.string,
    children: PropTypes.node
};

/**
 * The features header, renders a layout with passed in product data
 * @param {Object} props - properties to render the component
 * @param {Array} props.xs - componentLists for xs breakpoint and above
 * @param {Object} props.stateProps - The stateful product object passed in
 * @returns {Node} the features header affixed component with optional label and quote headers
 * @constructor
 */
const LayoutFeatureHeaderCell = ({ xs, stateProps }) => {
    const [
        brandLogo,
        compareToggleContainer,
        productName,
        productNameXs,
        quotePrice,
        quotePriceXs,
        excessLabel,
        homeContentExcessFeaturesContainer,
        insurerHandoverButton,
        keyInformation,
        callOptionsToggleFeatures,
        floatingCallOptions,
        specialOfferStacked,
        layoutFeatureHeaderCellDisclaimer
    ] = xs;

    const { constructComponents } = pageContext.getProps().director;
    const { product } = stateProps;
    return product
        ? (
            <div className={styles.root}>
                <SimpleFlexbox className={styles.container} column display="flex">
                    <SimpleFlexbox className={styles.row} display="flex" padding="all">
                        <BalanceHeightWrapper rowId="brandLogo" isResizable>
                            <SimpleFlexbox flex={3} display="flex" itemsCenter="xs" contentCenter="xs" variant="text_align_center">
                                {constructComponents(brandLogo, { stateProps })}
                            </SimpleFlexbox>
                        </BalanceHeightWrapper>
                        <SimpleFlexbox display="block" xs={0}>
                            {constructComponents(compareToggleContainer, { stateProps })}
                        </SimpleFlexbox>
                    </SimpleFlexbox>
                    <BalanceHeightWrapper rowId="productName" isResizable>
                        <SimpleFlexbox className={styles.row} xs={0} column display="flex" padding="bottom" contentBetween="sm">
                            {constructComponents(productName, { stateProps })}
                        </SimpleFlexbox>
                        <SimpleFlexbox className={styles.row} xs={0} column display="flex" padding="bottom" contentBetween="sm">
                            {constructComponents(layoutFeatureHeaderCellDisclaimer, { stateProps })}
                        </SimpleFlexbox>
                    </BalanceHeightWrapper>
                    <BalanceHeightWrapper rowId="productNameXs" isResizable>
                        <SimpleFlexbox className={styles.row} sm={0} md={0} lg={0} xl={0} xxl={0} column display="flex" padding="bottom" contentBetween="xs">
                            {constructComponents(productNameXs, { stateProps })}
                        </SimpleFlexbox>
                        <SimpleFlexbox className={styles.row} sm={0} md={0} lg={0} xl={0} xxl={0} column display="flex" padding="bottom" contentBetween="xs">
                            {constructComponents(layoutFeatureHeaderCellDisclaimer, { stateProps })}
                        </SimpleFlexbox>
                    </BalanceHeightWrapper>
                    <SimpleFlexbox className={styles.row} xs={0} padding="left_right" margin="top_bottom" variant="text_align_left">
                        {constructComponents(quotePrice, { stateProps })}
                    </SimpleFlexbox>
                    <SimpleFlexbox className={styles.row} flex={1} margin="top" sm={0} md={0} lg={0} xl={0} xxl={0}>
                        {constructComponents(quotePriceXs, { stateProps })}
                    </SimpleFlexbox>
                    <SimpleFlexbox className={styles.row}>
                        {constructComponents(excessLabel, { stateProps })}
                    </SimpleFlexbox>
                    {homeContentExcessFeaturesContainer.length > 0 ? (
                        <BalanceHeightWrapper rowId="homeContentExcessFeaturesContainer">
                            <SimpleFlexbox className={styles.row} display="flex" contentCenter="xs">
                                <SimpleFlexbox xs={6} column display="flex" contentCenter="xs" itemsCenter="xs">
                                    {constructComponents(homeContentExcessFeaturesContainer, { stateProps })}
                                </SimpleFlexbox>
                            </SimpleFlexbox>
                        </BalanceHeightWrapper>
                    ) : null}
                    <SimpleFlexbox className={styles.row} padding="all">
                        {constructComponents(insurerHandoverButton, { stateProps })}
                    </SimpleFlexbox>
                    <SimpleFlexbox className={styles.row}>
                        {constructComponents(keyInformation, { stateProps })}
                    </SimpleFlexbox>
                    <BalanceHeightWrapper rowId="callOptionsToggleFeatures" flex="1 0 auto" justifyContent="flex-end">
                        <SimpleFlexbox className={styles.row} display="flex" position="relative" margin="top" itemsCenter="xs" contentCenter="xs">
                            {constructComponents(callOptionsToggleFeatures, { stateProps })}
                        </SimpleFlexbox>
                    </BalanceHeightWrapper>
                    {/* nested div needed here for position absolute to work with drop down menu on call me back toggle */}
                    <div>
                        {constructComponents(floatingCallOptions, { stateProps })}
                    </div>
                    <BalanceHeightWrapper rowId="specialOffer" isResizable>
                        <SimpleFlexbox className={styles.row_grow}>
                            {constructComponents(specialOfferStacked, { stateProps })}
                        </SimpleFlexbox>
                    </BalanceHeightWrapper>
                </SimpleFlexbox>
            </div>
        )
        : <SimpleFlexbox flex={1} />;
};

LayoutFeatureHeaderCell.propTypes = {
    xs: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object
};

export { LayoutFeatureHeaderCell };
